import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useState } from "react";
import { useNavigate } from "react-router";
import * as XLSX from "xlsx";
import Header from "../../components/header";
import Drawer from "../home/components/drawer";

import { CONSTANTS } from "../../constants";
import { fetcher } from "../../network";
import { API } from "../../network/api";
import { patientsQueryKey } from "../../network/globalFetches";
import { PatientsResponse } from "../../types/responses";
import FloatingButtonContainer from "../../components/floatingButton";
import { FaRegFileExcel } from "react-icons/fa";

const fetchPatients = async () => {
  const { data } = await fetcher().get<PatientsResponse>(API.GET.PATIENTS);
  return data.patients;
};

const EnterExitPatientsPage = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();

  let { data: patients, isError } = useQuery({
    queryKey: patientsQueryKey,
    queryFn: fetchPatients,
  });

  const toggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev);
  };

  if (isError) {
    navigate(CONSTANTS.ROUTES.home, { replace: true });
    return <p>שגיאה..</p>;
  }

  const exportToExcel = () => {
    if (!patients) {
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(
      patients.map((patient) => ({
        "שם הדייר": patient.name,
        "תאריך כניסה": dayjs(patient.enterDateToHome).format("DD/MM/YYYY"),
        "תאריך יציאה": dayjs(patient.enterDateToHome).format("DD/MM/YYYY"),
        "ימים לסיום": dayjs(patient.exitDateFromHome).diff(dayjs(), "day"),
        "סה״כ ימי שהות מהתחלה לסוף": dayjs(patient.exitDateFromHome).diff(
          patient.enterDateToHome,
          "day"
        ),
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Patients");

    // Save file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "patients.xlsx");
  };

  return (
    <div className="container mx-auto relative h-screen">
      <Header>
        <Header.Time />
        <Header.Title title="כניסה ויציאת דיירים" />
        <Header.Menu toggleDrawer={toggleDrawer} />
      </Header>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow className="bg-blue-200">
              <TableCell>
                <p className="font-bold">מס׳</p>
              </TableCell>
              <TableCell>
                <p className="font-bold">שם הדייר</p>
              </TableCell>
              <TableCell>
                <p className="font-bold">תאריך כניסה</p>
              </TableCell>
              <TableCell>
                <p className="font-bold">תאריך יציאה</p>
              </TableCell>
              <TableCell>
                <p className="font-bold">מס ימים לסיום שהות</p>
              </TableCell>
              <TableCell>
                <p className="font-bold">סה״כ ימי שהות</p>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {patients?.map((patient, index) => {
              const daysToEnd = dayjs(patient.exitDateFromHome).diff(
                dayjs(),
                "day"
              );
              const fromStartToEnd = dayjs(patient.exitDateFromHome).diff(
                patient.enterDateToHome,
                "day"
              );

              const num = (index + 1).toString();

              return (
                <TableRow key={patient._id}>
                  <TableCell>{num}</TableCell>
                  <TableCell>{patient.name}</TableCell>
                  <TableCell>
                    {dayjs(patient.enterDateToHome).format("DD/MM/YYYY")}
                  </TableCell>
                  <TableCell>
                    {patient.exitDateFromHome
                      ? dayjs(patient.exitDateFromHome).format("DD/MM/YYYY")
                      : "-"}
                  </TableCell>
                  <TableCell>{daysToEnd ? `${daysToEnd} ימים` : "-"}</TableCell>
                  <TableCell>
                    {fromStartToEnd ? `${fromStartToEnd} ימים` : "-"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <FloatingButtonContainer>
        <Tooltip
          title="יצא קובץ אקסל"
          placement="right"
          className="hover:scale-105">
          <FloatingButtonContainer.Button
            icon={<FaRegFileExcel className="text-2xl" />}
            onClick={exportToExcel}
          />
        </Tooltip>
      </FloatingButtonContainer>

      {isDrawerOpen && <Drawer toggleDrawer={toggleDrawer} />}
    </div>
  );
};

export default EnterExitPatientsPage;
